import type { ComponentProps } from 'react';
import { InlineStack, Text } from '@customerio/pluma-components/react';
import type { Delivery } from '@/services/db/delivery/entity';
import { getDisplayState } from '@/services/db/delivery/utils';
import { channelValueOf } from '../../catalog';
import { DeliveryStateTag } from '../delivery-state-tag';

/** Transient text shown while an optimistic bulk op is in flight. */
function pendingStatus(
  delivery: Delivery,
): { label: string; color: NonNullable<ComponentProps<typeof Text>['color']> } | undefined {
  if ((delivery.sent ?? 0) > 0) return undefined;
  if (delivery.pendingDelete === true) return { label: 'Deleting soon', color: 'critical' };
  if (delivery.pendingRetry === true) return { label: 'Retrying soon', color: 'success' };
  if (delivery.pendingSend === true) return { label: 'Sending soon', color: 'success' };
  return undefined;
}

type StatusCellProps = { delivery: Delivery };

/**
 * "Status" cell (right-aligned). Shows a transient "…soon!" message while an
 * optimistic op is pending, otherwise the colored delivery-state tag derived
 * from the timestamps. Ported from Ember `cells/status`.
 */
export function StatusCell({ delivery }: StatusCellProps) {
  const pending = pendingStatus(delivery);
  const state = getDisplayState(delivery);

  return (
    <InlineStack alignInline="end" alignBlock="center" gap="100">
      {pending != null ? (
        <Text size="xs" color={pending.color}>
          {pending.label}
        </Text>
      ) : state != null ? (
        <DeliveryStateTag state={state} channel={channelValueOf(delivery.action_type)} />
      ) : null}
    </InlineStack>
  );
}
